import { ComponentProps, useState } from 'react';

import { Carousel, CarouselSlide } from '@/components/Carousel';
import { ProductImageFallback } from '@/components/ProductImageFallback';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/contexts/AuthContext';
import { cn, mountProductTags, priceFormatterV2 } from '@/lib/utils';
import { Product } from '@/services/ProductsService';

interface ProductCardProps {
  images: string[] | undefined;
  link: string | undefined;
  product: Product | undefined;
}

function Image(props: ComponentProps<'img'>) {
  const [error, setError] = useState(false);

  if (error) {
    return <ProductImageFallback />;
  }

  return (
    <img className="rounded-t-lg" {...props} onError={() => setError(true)} />
  );
}

export function ProductCard({ product, images, link }: ProductCardProps) {
  const { user } = useAuth();

  const formattedPrice = parseFloat(product?.price || '0');
  const formattedSalePrice = parseFloat(product?.salePrice || '0');
  let tags: string[] | undefined;
  if (product) {
    tags = mountProductTags(product);
  }
  const pairsWith = ['Pairs With', 'Compre o Look', 'Compra el Look'];

  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      className="flex flex-col rounded-lg border border-solid border-[#E0E0E0]"
    >
      <div>
        <Carousel navigation={{ enabled: true }}>
          {product?.fashionaiVideo && (
            <CarouselSlide>
              <video
                autoPlay
                muted
                loop
                playsInline
                className="h-auto w-full rounded-t-lg object-cover"
              >
                <source src={product.fashionaiVideo} type="video/mp4" />
              </video>
            </CarouselSlide>
          )}

          {images?.map((image, index) => (
            <CarouselSlide key={`${image}-${index}`}>
              {tags && tags.length > 0 && (
                <ul className="absolute left-2 top-2 z-10">
                  {tags.map((tag) => (
                    <li key={tag}>
                      <Badge
                        className={`${pairsWith.includes(tag) ? 'bg-green-500' : 'bg-red-500'}`}
                      >
                        {tag}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}

              <Image src={image} alt={product?.name} />
            </CarouselSlide>
          ))}
        </Carousel>
      </div>

      <div className="h-20 flex-col p-5 font-light">
        <span className="block truncate text-xs">{product?.name}</span>

        {formattedPrice > 0 && formattedSalePrice > 0 && (
          <div className="mt-1 flex gap-2 text-sm">
            {formattedSalePrice < formattedPrice && (
              <span className="line-through opacity-50">
                {priceFormatterV2(
                  formattedPrice,
                  user!.customer.language,
                  user!.customer.platform,
                )}
              </span>
            )}

            <span
              className={cn(
                'opacity-90',
                formattedSalePrice < formattedPrice &&
                  'font-semibold text-red-400',
              )}
            >
              {priceFormatterV2(
                formattedSalePrice,
                user!.customer.language,
                user!.customer.platform,
              )}
            </span>
          </div>
        )}
      </div>
    </a>
  );
}
